import jwtDecode from 'jwt-decode';

export type ParseStoredJsonResult<T> = {
  error: Error | null;
  value: T;
};

export type PersistedTokenState =
  | {
      error: null;
      refreshToken?: string;
      status: 'expired' | 'valid';
      token: string;
    }
  | {
      error: Error | null;
      refreshToken?: undefined;
      status: 'invalid' | 'missing';
      token?: undefined;
    };

type JwtPayload = {
  exp?: number;
};

const normalizeBootstrapError = (
  error: unknown,
  fallbackMessage: string,
): Error => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string' && error.trim().length > 0) {
    return new Error(error);
  }

  return new Error(fallbackMessage);
};

export const parseStoredJson = <T>(
  rawValue: string | null | undefined,
  fallbackValue: T,
): ParseStoredJsonResult<T> => {
  if (typeof rawValue !== 'string' || rawValue.trim().length === 0) {
    return {
      error: null,
      value: fallbackValue,
    };
  }

  try {
    return {
      error: null,
      value: JSON.parse(rawValue) as T,
    };
  } catch (error) {
    return {
      error: normalizeBootstrapError(error, 'Failed to parse persisted JSON'),
      value: fallbackValue,
    };
  }
};

export const parseStoredStringValue = (
  rawValue: string | null | undefined,
): string | null => {
  if (typeof rawValue !== 'string' || rawValue.trim().length === 0) {
    return null;
  }

  const parsed = parseStoredJson<unknown>(rawValue, rawValue);
  if (typeof parsed.value === 'string' && parsed.value.trim().length > 0) {
    return parsed.value;
  }

  if (typeof parsed.value === 'number' && Number.isFinite(parsed.value)) {
    return `${parsed.value}`;
  }

  return rawValue;
};

export const getPersistedTokenState = (
  rawValue: string | null | undefined,
  now = Date.now(),
): PersistedTokenState => {
  if (typeof rawValue !== 'string' || rawValue.trim().length === 0) {
    return {
      error: null,
      status: 'missing',
    };
  }

  const parsed = parseStoredJson<{refreshToken?: unknown; token?: unknown} | null>(
    rawValue,
    null,
  );

  if (parsed.error) {
    return {
      error: parsed.error,
      status: 'invalid',
    };
  }

  const token = parsed.value?.token;
  if (typeof token !== 'string' || token.trim().length === 0) {
    return {
      error: new Error('Persisted auth token is missing a token string'),
      status: 'invalid',
    };
  }

  let decodedToken: JwtPayload;
  try {
    decodedToken = jwtDecode<JwtPayload>(token);
  } catch (error) {
    return {
      error: normalizeBootstrapError(
        error,
        'Persisted auth token could not be decoded',
      ),
      status: 'invalid',
    };
  }

  if (typeof decodedToken.exp !== 'number') {
    return {
      error: new Error('Persisted auth token is missing exp'),
      status: 'invalid',
    };
  }

  return {
    error: null,
    refreshToken:
      typeof parsed.value?.refreshToken === 'string'
        ? parsed.value.refreshToken
        : undefined,
    status: decodedToken.exp * 1000 < now ? 'expired' : 'valid',
    token,
  };
};
